import React from 'react';
import { Box, Typography, Snackbar } from '@mui/joy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { EditableText } from './EditableText';
import { InlineEditControls } from '@/components/InlineEditControls';
import { DeleteAction } from '@/components/DeleteAction';
import { useUpdateSubcategoryMutation, useDeleteSubcategoryMutation } from '../api';
import { EmojiDisplay } from '@/components/EmojiDisplay';
import { getCategoryErrorMessage } from '../utils';

export function Subcategory({ subcategory, isEditing, onEditStart, onEditEnd }) {
  const [updateSubcategory] = useUpdateSubcategoryMutation();
  const [editName, setEditName] = React.useState(subcategory.name);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (isEditing) {
      setEditName(subcategory.name);
      setErrorMessage(null);
      setIsSaving(false);
    }
  }, [isEditing, subcategory.name]);

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await updateSubcategory({
        id: subcategory.apiId,
        data: { name: editName }
      }).unwrap();
      setIsSaving(false);
      onEditEnd({ ...subcategory, name: editName });
    } catch (error) {
      setIsSaving(false);
      setErrorMessage(getCategoryErrorMessage(error, 'update', 'subcategory'));
    }
  };

  const handleCancel = () => {
    setEditName(subcategory.name);
    onEditEnd();
  };

  return (    
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5, px: 1, borderRadius: 'sm', bgcolor: 'background.level1' }}>
      <Box sx={{ cursor: 'move', display: 'flex', alignItems: 'center', touchAction: 'none', mr: 0.5 }}>
        <DragIndicatorIcon />
      </Box>
      {isEditing ? (
        <>
          {subcategory.emoji && <Typography level="title-sm"><><EmojiDisplay emoji={subcategory.emoji} size={20} /> </></Typography>}
          <InlineEditControls
            value={editName}
            onChange={setEditName}
            onSave={handleSave}
            onCancel={handleCancel}
            size="sm"
            isPersisting={isSaving}
          />
        </>
      ) : (
        <>
          <EditableText
            value={subcategory.name}
            emoji={subcategory.emoji}
            level="title-sm"
            onEdit={onEditStart}            
          />
          <DeleteAction
            id={subcategory.apiId}
            name={subcategory.name}
            useDelete={useDeleteSubcategoryMutation}
            errorOverrides={{
              409: 'Cannot delete subcategory because it contains ingredients.'
            }}
          />
        </>
      )}
      <Snackbar
        color="danger"
        variant="soft"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
      >
        {errorMessage}
      </Snackbar>    
    </Box>    
  );
}
